import React, { useMemo, useCallback } from 'react';
import { Typography, makeStyles, createStyles } from '@material-ui/core';
import { parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { DataTable, IDataTableColumn, DateDisplay, EnumValueDisplay } from '@lib/common';
import { BulkChangeBillingStatus } from 'app/state';

interface Props {
  statuses: BulkChangeBillingStatus[];
}

const useStyles = makeStyles(theme =>
  createStyles({
    tableCell: {
      whiteSpace: 'nowrap',
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    errorCell: {
      maxWidth: 300,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
);

export const BulkChangeBillingStatusTable: React.FC<Props> = ({ statuses }) => {
  const styles = useStyles();

  const sortedStatuses = useMemo(
    () => [...statuses].sort((a, b) => (b.recordNumber ?? 0) - (a.recordNumber ?? 0)),
    [statuses]
  );

  const renderCell = useCallback(
    (data?: string | number | null) => (
      <Typography className={styles.tableCell}>{data ?? '—'}</Typography>
    ),
    [styles]
  );

  const columns: IDataTableColumn<BulkChangeBillingStatus>[] = useMemo(
    () => [
      {
        title: 'Record #',
        valueFactory: status => (
          <Link className={styles.link} to={`/reservations/${status.reservationId}`}>
            {status.recordNumber}
          </Link>
        ),
      },
      {
        title: 'Guest Name',
        valueFactory: status => renderCell(status.guestName),
      },
      {
        title: 'Status',
        valueFactory: status => (
          <EnumValueDisplay className={styles.tableCell} value={status.status} />
        ),
      },
      {
        title: 'Processed At',
        valueFactory: status =>
          status.processedAt ? (
            <DateDisplay date={parseISO(status.processedAt)} dateFormat="M/d/yy h:mm a" />
          ) : (
            '—'
          ),
      },
      {
        title: 'Error',
        valueFactory: status => (
          <Typography className={styles.errorCell} title={status.errorMessage ?? undefined}>
            {status.errorMessage ?? '—'}
          </Typography>
        ),
      },
    ],
    [styles, renderCell]
  );

  return (
    <DataTable items={sortedStatuses} columns={columns} getItemKey={status => status.id ?? ''} />
  );
};
